import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Loader2, FileText, MessageCircle, Phone, Mail, MapPin, Clock, CheckCircle2, RefreshCw, Truck, Calendar as CalIcon, Users, Download, Plus, Trash2, Pencil, KeyRound, X as XIcon, Ban, RotateCcw, Image as ImageIcon, Eye, PoundSterling, CalendarClock, Search, LayoutList, LayoutGrid, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { withOverride, errMsg } from '../components/porters/porterUi';
import { useAuth, formatApiError } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { VanRota } from '../components/staff/VanRota';
import { AdminEnquiries } from '../components/AdminEnquiries';
import { paidToast, DepositProof, DepositCheckRow } from '../components/AdminPayments';
import { OfferTapsCard } from '../components/OfferTaps';
import { DepositRefunds } from '../components/DepositRefunds';
import { CancellationReport } from '../components/CancellationReport';
import { RebookButton } from '../components/RebookButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
  } catch (_) { return iso; }
};

const nudgeWhatsApp = (item) => {
  const txt = encodeURIComponent(
    `Hi ${item.name || 'there'}, Move Van Ltd here — following up on your quote #${item.ref} (£${item.price}). Your 20% deposit of £${item.deposit} locks in the slot: https://pay.tide.co/pay/6d065db5-baf2-4969-8d33-1ad5ca43f6a3`
  );
  return `https://wa.me/${(item.phone || '').replace(/\D/g, '').replace(/^0/, '44')}?text=${txt}`;
};
const customerWhatsApp = (item) => {
  const txt = encodeURIComponent(`Hi ${item.name || 'there'}, Move Van Ltd here regarding your booking #${item.ref}.`);
  return `https://wa.me/${(item.phone || '').replace(/\D/g, '').replace(/^0/, '44')}?text=${txt}`;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolvePhoto = (p) => (!p ? '' : /^https?:/.test(p) ? p : `${BACKEND}${p}`);

const CANCEL_REASONS = [
  { value: 'customer_request', label: 'Customer request' },
  { value: 'weather', label: 'Weather' },
  { value: 'van_issue', label: 'Van issue' },
  { value: 'other', label: 'Other' },
];

// Detect waste/clearance jobs so we can colour-code them
const isWasteJob = (item) => {
  const haystack = item?.service ? item.service : [item?.van_recommendation, item?.property_size]
    .filter(Boolean).join(' ').toLowerCase();
  return /clearance|waste|rubbish/.test(haystack);
};

/* -------- Cancel modal -------- */
const CancelModal = ({ item, onClose, onConfirm }) => {
  const [reason, setReason] = useState('customer_request');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onConfirm({ reason, note: note.trim() || null });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="cancel-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white rounded-xl shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)]">Cancel booking #{item.ref}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 mb-3">
          Heads up: the deposit is <strong>non-refundable</strong>. The driver slot will be freed immediately.
        </div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm mb-3" data-testid="cancel-reason-select">
          {CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Note (optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="e.g. flat tyre on van 4" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mb-4" data-testid="cancel-note-input" />
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900">Keep booking</button>
          <button type="submit" disabled={saving} className="h-10 px-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-2" data-testid="cancel-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancel booking
          </button>
        </div>
      </form>
    </div>
  );
};

/* -------- Photo strip + Lightbox -------- */
const Lightbox = ({ photos, index, onClose, onNavigate }) => {
  const url = resolvePhoto(photos[index]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNavigate((index + 1) % photos.length);
      else if (e.key === 'ArrowLeft') onNavigate((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [index, photos.length, onClose, onNavigate]);
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <XIcon className="w-5 h-5" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate((index - 1 + photos.length) % photos.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate((index + 1) % photos.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <div className="absolute top-4 left-4 bg-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full">
        {index + 1} / {photos.length}
      </div>
      <img
        src={url}
        alt={`Waste photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
        onError={(e) => {
          e.currentTarget.replaceWith(Object.assign(document.createElement('div'), {
            className: 'text-white/80 text-sm bg-white/10 rounded-lg px-6 py-10 text-center max-w-sm',
            textContent: 'This photo is no longer available on the server.',
          }));
        }}
      />
    </div>
  );
};

const PhotoThumb = ({ src, label, onClick }) => {
  const [broken, setBroken] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 hover:border-[color:var(--mv-orange)] hover:shadow-md transition group"
      data-testid="quote-photo-thumb"
    >
      {broken ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-[9px] font-bold uppercase tracking-wider p-1 text-center leading-tight">
          <ImageIcon className="w-5 h-5 mb-1" />
          Unavailable
        </div>
      ) : (
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          onError={() => setBroken(true)}
        />
      )}
      {!broken && (
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Eye className="w-5 h-5 text-white drop-shadow" />
        </span>
      )}
    </button>
  );
};

const PhotoStrip = ({ photos }) => {
  const [lbIndex, setLbIndex] = useState(-1);
  if (!photos || photos.length === 0) return null;
  return (
    <>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5" data-testid="quote-photos">
        {photos.map((p, i) => (
          <PhotoThumb key={i} src={resolvePhoto(p)} label={`Waste photo ${i + 1}`} onClick={() => setLbIndex(i)} />
        ))}
      </div>
      {lbIndex >= 0 && (
        <Lightbox
          photos={photos}
          index={lbIndex}
          onClose={() => setLbIndex(-1)}
          onNavigate={setLbIndex}
        />
      )}
    </>
  );
};

/* -------- Booking detail modal (View) -------- */
const DetailModal = ({ id, onClose, authFetch, onAction }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authFetch('get', `/admin/estimate/${id}`);
        if (!cancelled) setData(data);
      } catch { if (!cancelled) toast.error("Couldn't load that booking."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, authFetch]);

  const act = async (fn, label) => {
    try { const msg = await fn(); toast.success(typeof msg === 'string' ? msg : label); onAction?.(); onClose(); }
    catch { toast.error('Something went wrong.'); }
  };

  const inp = data?.input || {};
  const out = data?.output || {};
  const b = data?.booking;
  const items = inp.items || inp.waste_items || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose} data-testid="detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-xl shadow-xl my-10">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl">
          <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">Booking #{data?.ref || '…'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><XIcon className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-500">Not found.</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm">
              <Row label="Customer" value={inp.name || '—'} />
              <Row label="Phone" value={inp.phone ? <a className="text-[color:var(--mv-orange)]" href={`tel:${inp.phone}`}>{inp.phone}</a> : '—'} />
              <Row label="Email" value={inp.email ? <a className="text-[color:var(--mv-orange)]" href={`mailto:${inp.email}`}>{inp.email}</a> : '—'} />
              <Row label="Service" value={inp.service || '—'} />
              <Row label="From" value={inp.from_postcode || '—'} />
              <Row label="To" value={inp.to_postcode || '—'} />
              <Row label="Van" value={out.van_recommendation || inp.van_preference || '—'} />
              <Row label="Hours" value={out.hours_estimate || '—'} />
              <Row label="Distance" value={out.distance_miles ? `${out.distance_miles} mi` : '—'} />
              <Row label="Volume" value={out.volume_m3 ? `${out.volume_m3} m³` : '—'} />
              <Row label="Price" value={<strong>£{out.price}</strong>} />
              <Row label="Deposit" value={`£${out.deposit}`} />
              {b && <>
                <Row label="Booked for" value={<strong>{b.date} at {b.time}</strong>} />
                <Row label="Final price" value={b.final_price != null ? <strong>£{b.final_price}</strong> : '—'} />
              </>}
              <Row label="Deposit status" value={
                data.deposit_marked_paid
                  ? <span className="text-emerald-700 font-bold">Paid (checked)</span>
                  : data.deposit_claimed_at
                    ? <span className="text-amber-700 font-bold" data-testid="detail-deposit-claimed">Confirm booking deposit</span>
                    : <span className="text-amber-700 font-bold">Awaiting</span>
              } />
              <DepositProof item={{ ...data, ref: (data.id || '').slice(0, 6).toUpperCase() }} />
              {data.completed_at && <Row label="Completed" value={<span className="text-blue-700 font-bold">{new Date(data.completed_at).toLocaleString()}</span>} />}
              {data.cancelled_at && <Row label="Cancelled" value={<span className="text-red-700 font-bold">{data.cancellation?.reason_label}</span>} />}
            </div>

            {items?.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Items</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{Array.isArray(items) ? items.map(i => typeof i === 'string' ? i : `${i.name || ''}${i.qty ? ' ×' + i.qty : ''}`).join(' · ') : String(items)}</div>
              </div>
            )}

            {inp.notes && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Customer notes</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{inp.notes}</div>
              </div>
            )}

            {data.photos?.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Waste photos ({data.photos.length}) · tap to enlarge</div>
                <PhotoStrip photos={data.photos} />
              </div>
            )}

            <div className="pt-4 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
              {!data.deposit_marked_paid && !data.cancelled_at && (
                <button onClick={() => act(async () => { const r = await authFetch('post', `/admin/estimate/${id}/mark-paid`); return `Deposit ${paidToast(r?.data)}`; }, 'Marked as paid.')} className="px-3 h-9 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="detail-mark-paid"><CheckCircle2 className="w-4 h-4" /> {data.deposit_claimed_at ? 'Checked in Tide · mark paid' : 'Mark deposit paid'}</button>
              )}
              {data.deposit_claimed_at && !data.cancelled_at && !data.completed_at && (
                <button onClick={() => act(() => authFetch('post', `/admin/estimate/${id}/mark-unpaid`), 'Deposit unmarked.')} className="px-3 h-9 rounded-md bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-xs font-bold inline-flex items-center gap-1.5">{data.deposit_marked_paid ? 'Unmark paid' : 'Clear customer payment claim'}</button>
              )}
              {b && !data.cancelled_at && !data.completed_at && (
                <button onClick={() => act(() => authFetch('post', `/admin/estimate/${id}/complete`), 'Booking completed.')} className="px-3 h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="detail-complete"><CheckCircle2 className="w-4 h-4" /> Mark completed</button>
              )}
              {data.completed_at && (
                <button onClick={() => act(() => authFetch('post', `/admin/estimate/${id}/uncomplete`), 'Reopened.')} className="px-3 h-9 rounded-md bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-bold">Reopen</button>
              )}
              <button
                onClick={() => {
                  if (!window.confirm(`Permanently delete booking #${data.ref}? This cannot be undone.`)) return;
                  act(() => authFetch('delete', `/admin/estimate/${id}`), 'Deleted.');
                }}
                className="px-3 h-9 rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-bold inline-flex items-center gap-1.5" data-testid="detail-delete"
              ><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-slate-800 mt-0.5">{value}</div>
  </div>
);

/* -------- Edit booking date/time modal -------- */
const EditBookingModal = ({ item, onClose, onSave }) => {
  const [date, setDate] = useState(item.booking?.date || '');
  const [time, setTime] = useState(item.booking?.time || '');
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);
  const hasEmail = !!item.email;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try { await onSave({ date, time, notify: notify && hasEmail }); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="edit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-sm bg-white rounded-xl shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)]">{item.booking ? 'Reschedule' : 'Add to calendar'} #{item.ref}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 mb-3">
          {item.booking ? 'Driver assignment will be refreshed for the new date automatically.' : 'A free driver is assigned automatically. You can change the driver afterwards.'}
        </div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm mb-3" data-testid="edit-date-input" />
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Time</label>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm mb-4" data-testid="edit-time-input" />
        <label className={`flex items-center gap-2 text-xs mb-4 ${hasEmail ? 'text-slate-700 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`}>
          <input type="checkbox" disabled={!hasEmail} checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 accent-[color:var(--mv-navy)]" data-testid="edit-notify-check" />
          <span>{item.booking ? 'Email the customer about the change' : 'Email the customer their booking confirmation'}{hasEmail ? '' : ' (no email on file)'}</span>
        </label>
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900">Cancel</button>
          <button type="submit" disabled={saving} className="h-10 px-4 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="edit-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Save
          </button>
        </div>
      </form>
    </div>
  );
};

/* -------- Revenue strip -------- */
const RevenueStrip = ({ revenue }) => {
  if (!revenue) return null;
  const r = revenue;
  const cells = [
    { label: 'Revenue today', value: r.today, cls: 'from-emerald-500 to-emerald-700', test: 'rev-today' },
    { label: 'Week-to-date', value: r.wtd, cls: 'from-emerald-600 to-emerald-800', test: 'rev-wtd' },
    { label: 'Month-to-date', value: r.mtd, cls: 'from-[color:var(--mv-navy)] to-[#061a33]', test: 'rev-mtd' },
    { label: 'Outstanding deposits', value: r.outstanding_deposits, cls: 'from-amber-500 to-amber-700', test: 'rev-outstanding' },
    { label: 'Collected today', value: r.collected_today?.total, cls: 'from-teal-500 to-teal-700', test: 'rev-collected', sub: r.collected_today && `Cash £${r.collected_today.cash} · Card £${r.collected_today.card} · Bank £${r.collected_today.bank}` },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="revenue-strip">
      {cells.map(c => (
        <div key={c.label} data-testid={c.test} className={`rounded-xl p-4 text-white bg-gradient-to-br ${c.cls} shadow-sm`}>
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 flex items-center gap-1"><PoundSterling className="w-3 h-3" />{c.label}</div>
          <div className="font-display text-3xl font-black mt-1 leading-none">£{Number(c.value || 0).toLocaleString()}</div>
          {c.sub && <div className="text-[10px] mt-1 opacity-90" data-testid={`${c.test}-breakdown`}>{c.sub}</div>}
        </div>
      ))}
    </div>
  );
};

const DayHeader = ({ date, todayIso }) => {
  if (!date) return null;
  const t = todayIso || new Date().toISOString().slice(0, 10);
  const tag = date < t ? 'Past — not marked done' : date === t ? 'Today' : null;
  const label = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className={`mt-3 mb-1 text-xs font-black uppercase tracking-wider ${date < t ? 'text-red-700' : 'text-slate-500'}`} data-testid={`all-day-${date}`}>
      {label}{tag && <span className={`ml-2 px-2 py-0.5 rounded-full normal-case ${date < t ? 'bg-red-100' : 'bg-emerald-100 text-emerald-800'}`}>{tag}</span>}
    </div>
  );
};

/* -------- Driver 7-day availability grid -------- */
const AvailabilityGrid = ({ availability }) => {
  if (!availability || availability.length === 0) return null;
  const drivers = availability[0]?.drivers || [];
  if (drivers.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 overflow-x-auto" data-testid="availability-grid">
      <div className="min-w-[720px]">
        <div className="grid gap-1" style={{ gridTemplateColumns: `160px repeat(${availability.length}, minmax(72px, 1fr))` }}>
          <div></div>
          {availability.map((d) => {
            const dt = new Date(d.date + 'T00:00:00');
            const day = dt.toLocaleDateString('en-GB', { weekday: 'short' });
            const num = dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            return (
              <div key={d.date} className="text-center">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{day}</div>
                <div className="text-xs text-slate-700 font-bold">{num}</div>
              </div>
            );
          })}
          {drivers.map((driver, idx) => (
            <React.Fragment key={driver.id}>
              <div className="flex items-center gap-2 py-1">
                <span className="w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-black" style={{ background: driver.color }}>{(driver.name || '?').slice(0,1).toUpperCase()}</span>
                <span className="text-sm font-bold text-[color:var(--mv-navy)] truncate">{driver.name}</span>
              </div>
              {availability.map((d) => {
                const slot = d.drivers[idx];
                if (slot.status === 'off' && slot.jobs === 0) {
                  return <div key={d.date + driver.id} className="h-9 rounded-md text-[10px] font-black uppercase flex items-center justify-center bg-red-100 text-red-800" title={`${driver.name} is off`} data-testid={`avail-off-${driver.id.slice(0, 6)}-${d.date}`}>{slot.off_label || 'Off'}</div>;
                }
                if (slot.jobs === 0) {
                  return <div key={d.date + driver.id} className="h-9 rounded-md text-xs flex items-center justify-center bg-white border border-emerald-100 text-emerald-800">—</div>;
                }
                return (
                  <div key={d.date + driver.id} className={`h-9 rounded-md border flex items-center justify-center gap-1 overflow-hidden ${slot.status === 'off' ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'}`} title={slot.status === 'off' ? `${slot.off_label} but booked — arrange cover` : `${slot.waste} waste · ${slot.ab} A-B`} data-testid={`avail-slot-${driver.id.slice(0, 6)}-${d.date}`}>
                    {slot.status === 'off' && <span className="text-[9px] font-black text-red-700">OFF!</span>}
                    {slot.waste > 0 && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white">W{slot.waste}</span>}
                    {slot.ab > 0 && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-300 text-amber-900">AB{slot.ab}</span>}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-emerald-100"></span>Free</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-violet-600"></span>Waste / clearance</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-300"></span>A-B move</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-100 border border-red-200"></span>Day off / holiday</span>
        </div>
      </div>
    </div>
  );
};

/* -------- Timeline (day-by-day grouping) -------- */
const Timeline = ({ bookings, drivers, onView }) => {
  const groups = useMemo(() => {
    const m = new Map();
    bookings.forEach(b => {
      const key = b.booking?.date || '—';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(b);
    });
    return [...m.entries()].sort((a,b) => a[0] < b[0] ? -1 : 1);
  }, [bookings]);
  if (groups.length === 0) return <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">No bookings in the timeline.</div>;
  return (
    <div className="space-y-4" data-testid="timeline">
      {groups.map(([date, items]) => (
        <div key={date} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
            <CalIcon className="w-4 h-4 text-[color:var(--mv-orange)]" />
            <strong className="text-[color:var(--mv-navy)]">{new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}</strong>
            <span className="text-xs text-slate-500">· {items.length} booking{items.length === 1 ? '' : 's'}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {items.sort((a,b) => (a.booking?.time || '') < (b.booking?.time || '') ? -1 : 1).map(it => {
              const d = drivers?.find(x => x.id === it.driver_id);
              return (
                <button key={it.id} onClick={() => onView(it.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition">
                  <div className="font-black text-[color:var(--mv-navy)] text-sm w-14">{it.booking?.time || '—'}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">#{it.ref} · {it.name || '—'}</div>
                    <div className="text-xs text-slate-500 truncate">{it.from_postcode || '—'} → {it.to_postcode || '—'} · {it.service || '—'}</div>
                  </div>
                  {d && <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: d.color }}>{d.name}</span>}
                  <div className="font-black text-[color:var(--mv-navy)]">£{it.booking?.final_price ?? it.price}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* -------- Staff notes editable (internal only) -------- */
const StaffNotes = ({ initial, onSave }) => {
  const [value, setValue] = useState(initial || '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setValue(initial || ''); }, [initial]);

  const save = async () => {
    if ((value || '').trim() === (initial || '').trim()) { setEditing(false); return; }
    await onSave(value.trim());
    setEditing(false);
  };

  if (!editing && !value) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="mt-2 text-[11px] font-bold text-slate-500 hover:text-[color:var(--mv-orange)] inline-flex items-center gap-1" data-testid="staff-notes-add">
        <Pencil className="w-3 h-3" /> Add staff note (internal only)
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-md bg-indigo-50 border border-indigo-200 p-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-1">
        <strong className="text-[10px] font-black uppercase tracking-wider text-indigo-800">🔒 Staff note (internal)</strong>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-[10px] font-bold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1" data-testid="staff-notes-edit">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="e.g. ring second buzzer · lift at 8am · parking on Westfield Rd"
            className="w-full text-xs rounded border border-indigo-200 bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            data-testid="staff-notes-textarea"
            autoFocus
          />
          <div className="mt-1 flex items-center justify-end gap-2">
            <button onClick={() => { setValue(initial || ''); setEditing(false); }} className="text-[11px] font-bold text-slate-500 hover:text-slate-700">Cancel</button>
            <button onClick={save} className="text-[11px] font-bold bg-indigo-600 text-white px-3 py-1 rounded" data-testid="staff-notes-save">Save</button>
          </div>
        </>
      ) : (
        <div className="text-xs text-indigo-900 whitespace-pre-wrap">{value}</div>
      )}
    </div>
  );
};

/* -------- Quote card -------- */
const QuoteCard = ({ item, variant, onSchedule, onConfirm, drivers, onAssign, onCancel, onUncancel, onView, onMarkPaid, onMarkUnpaid, onComplete, onEditDate, onDelete, onRestore, onPermanentDelete, onSaveStaffNotes, selectable, selected, onToggleSelect, authFetch, onRebooked }) => {
  const sameDay = item.booking?.same_day;
  const assigned = drivers?.find(d => d.id === item.driver_id);
  const cancelled = !!item.cancelled_at;
  const completed = !!item.completed_at;
  const trashed = !!item.deleted_at;
  const paid = !!item.deposit_claimed_at;
  const waste = isWasteJob(item);
  const ab = !waste; // Any non-waste booking is an "A-B" move
  const enquiredAt = item.created_at ? new Date(item.created_at) : null;
  const enquiredFmt = enquiredAt ? enquiredAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const borderCls = trashed ? 'border-slate-300 opacity-60'
                 : cancelled ? 'border-red-200 opacity-70'
                 : completed ? 'border-blue-200'
                 : waste ? 'border-l-[6px] border-l-violet-600 border-y-slate-200 border-r-slate-200'
                 : ab ? 'border-l-[6px] border-l-amber-400 border-y-slate-200 border-r-slate-200'
                 : sameDay ? 'border-red-300'
                 : 'border-slate-200';
  const bgCls = trashed ? 'bg-slate-50'
             : waste && !cancelled ? 'bg-violet-50/40'
             : ab && !cancelled && !completed ? 'bg-amber-50/40'
             : 'bg-white';
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-4 shadow-sm`} data-testid="admin-quote-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {selectable && !trashed && (
          <label className="pt-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={!!selected} onChange={(e) => onToggleSelect?.(item.id, e.target.checked)} className="w-4 h-4 accent-[color:var(--mv-navy)]" data-testid="admin-select-check" />
          </label>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-[color:var(--mv-navy)] text-base">#{item.ref}</span>
            <span className={`text-slate-700 font-bold ${cancelled || trashed ? 'line-through' : ''}`}>{item.name || '(no name)'}</span>
            {waste && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-violet-600 text-white">Waste</span>}
            {ab && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-300 text-amber-900">A-B</span>}
            {trashed && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-slate-300 text-slate-700">Trashed</span>}
            {cancelled && !trashed && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-800">Cancelled · {item.cancellation?.reason_label}</span>}
            {!cancelled && !trashed && completed && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Completed</span>}
            {!cancelled && !trashed && !completed && paid && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Paid</span>}
            {!cancelled && !trashed && sameDay && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-800">Same-day</span>}
            {!cancelled && !trashed && item.owner_confirmed_at && !completed && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Confirmed</span>}
            {!cancelled && !trashed && assigned && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full text-white" style={{ background: assigned.color || '#64748b' }}>→ {assigned.name}</span>}
            {item.photos?.length > 0 && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" />{item.photos.length}</span>}
            {!cancelled && !trashed && item.job_status && item.job_status !== 'done' && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" data-testid="admin-job-status">{item.job_status === 'on_the_way' ? 'Driver on the way' : 'Driver arrived'}</span>}
            {item.balance_collected && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-teal-100 text-teal-800" data-testid="admin-balance-collected">£{item.balance_collected.amount} collected · {({ cash: 'Cash', card: 'Card', bank: 'Bank transfer' })[item.balance_collected.method] || item.balance_collected.method}</span>}
            {item.signature_url && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid="admin-signed">Signed ✓</span>}
            {item.report_sent_at && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-orange-100 text-orange-800" data-testid="admin-report-sent">Report sent</span>}
          </div>
          <div className="text-xs text-slate-600 mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
            {item.phone && <a href={`tel:${item.phone}`} className="flex items-center gap-1 hover:text-[color:var(--mv-orange)]"><Phone className="w-3 h-3" />{item.phone}</a>}
            {item.email && <a href={`mailto:${item.email}`} className="flex items-center gap-1 hover:text-[color:var(--mv-orange)]"><Mail className="w-3 h-3" />{item.email}</a>}
          </div>
          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {item.from_postcode || '—'} → {item.to_postcode || '—'}
          </div>
          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
            <Truck className="w-3 h-3" /> {item.van_recommendation || item.service || '—'}
            {item.hours_estimate && <> · {item.hours_estimate}</>}
            {item.property_size && <> · {item.property_size}</>}
            {item.items_count && <> · {item.items_count} items</>}
          </div>
          {/* Enquiry time + preferred move slot */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="w-3 h-3" />Enquired <strong className="text-slate-700">{enquiredFmt}</strong></span>
            {!item.booking && (item.preferred_date || item.preferred_time) && (
              <span className="inline-flex items-center gap-1 text-[color:var(--mv-navy)] bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5"><CalIcon className="w-3 h-3 text-[color:var(--mv-orange)]" />Wants <strong>{item.preferred_date || '—'}{item.preferred_time ? ' at ' + item.preferred_time : ''}</strong></span>
            )}
          </div>
          {item.booking && (
            <div className="mt-2 text-xs text-[color:var(--mv-navy)] font-semibold flex items-center gap-1">
              <CalIcon className="w-3 h-3 text-[color:var(--mv-orange)]" /> Booked: {item.booking.date} at {item.booking.time}
              {item.booking.final_price !== undefined && <span className="font-black ml-1">· £{item.booking.final_price}</span>}
            </div>
          )}
          {item.notes && (
            <div className="mt-2 text-xs rounded-md bg-slate-50 border border-slate-200 p-2 text-slate-700" data-testid="quote-notes">
              <strong className="text-[10px] font-black uppercase tracking-wider text-slate-500">Customer note</strong>
              <div className="mt-0.5 line-clamp-3 whitespace-pre-wrap">{item.notes}</div>
            </div>
          )}
          {onSaveStaffNotes && !trashed && (
            <StaffNotes initial={item.staff_notes || ''} onSave={(n) => onSaveStaffNotes(item, n)} />
          )}
          {cancelled && item.cancellation?.note && (
            <div className="mt-2 text-xs text-slate-500 italic">Cancel note: {item.cancellation.note}</div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="font-display text-2xl font-black text-[color:var(--mv-navy)] leading-none">£{item.booking?.final_price ?? item.price}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Deposit £{item.deposit}</div>
        </div>
      </div>

      <PhotoStrip photos={item.photos} />
      {(item.proof_photos?.length > 0 || item.signature_url) && (
        <div className="mt-2" data-testid="admin-proof">
          <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Proof of job</div>
          {['before', 'after'].map(k => {
            const ph = (item.proof_photos || []).filter(p => p.kind === k).map(p => p.url);
            return ph.length > 0 && (
              <div key={k} className="mt-1" data-testid={`admin-proof-${k}`}>
                <div className="text-[10px] font-bold text-slate-500 capitalize">{k} ({ph.length})</div>
                <PhotoStrip photos={ph} />
              </div>
            );
          })}
          {item.signature_url && (
            <div className="mt-1" data-testid="admin-proof-signature">
              <div className="text-[10px] font-bold text-slate-500">Customer signature</div>
              <PhotoStrip photos={[item.signature_url]} />
            </div>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        {item.booking && !trashed && (
          <a href={`${BACKEND}${item.report_url || `/api/jobs/${item.id}/report.pdf`}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-report-btn">
            <FileText className="w-3.5 h-3.5" /> Job report
          </a>
        )}
        {onView && (
          <button onClick={() => onView(item)} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-view-btn">
            <Eye className="w-3.5 h-3.5" /> View
          </button>
        )}
        {variant === 'awaiting' && !cancelled && !paid && (
          <>
            <button onClick={() => onMarkPaid?.(item)} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-mark-paid-btn">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid
            </button>
            <a href={nudgeWhatsApp(item)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebc5a] text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-nudge-btn">
              <MessageCircle className="w-3.5 h-3.5" /> Nudge
            </a>
          </>
        )}
        {variant === 'awaiting' && paid && !cancelled && (
          <button onClick={() => onMarkUnpaid?.(item)} className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-xs font-bold px-3 py-1.5 rounded-md">Unmark paid</button>
        )}
        {variant === 'paid-not-booked' && onSchedule && (
          <button onClick={() => onSchedule(item)} className="inline-flex items-center gap-1.5 bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-schedule-btn">
            <CalendarClock className="w-3.5 h-3.5" /> Add to calendar
          </button>
        )}
        {variant === 'booking' && !cancelled && !completed && !item.owner_confirmed_at && (
          <button onClick={() => onConfirm && onConfirm(item)} className="inline-flex items-center gap-1.5 bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-confirm-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
          </button>
        )}
        {variant === 'booking' && !cancelled && !completed && onComplete && (
          <button onClick={() => onComplete(item)} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-complete-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Done
          </button>
        )}
        {variant === 'booking' && !cancelled && !completed && onEditDate && (
          <button onClick={() => onEditDate(item)} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-edit-date-btn">
            <CalendarClock className="w-3.5 h-3.5" /> Edit date
          </button>
        )}
        {variant === 'booking' && !cancelled && !completed && onAssign && drivers && drivers.length > 0 && (
          <select
            value={item.driver_id || ''}
            onChange={(e) => onAssign(item, e.target.value || null)}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
            data-testid="admin-assign-select"
          >
            <option value="">Unassigned</option>
            {drivers.map(d => <option key={d.id} value={d.id}>→ {d.name}</option>)}
          </select>
        )}
        {variant === 'booking' && !cancelled && !completed && onCancel && (
          <button onClick={() => onCancel(item)} className="inline-flex items-center gap-1.5 bg-white border border-red-200 hover:border-red-300 text-red-700 text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-cancel-btn">
            <Ban className="w-3.5 h-3.5" /> Cancel
          </button>
        )}
        {variant === 'cancelled' && onUncancel && (
          <button onClick={() => onUncancel(item)} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-uncancel-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </button>
        )}
        {(variant === 'trash' || variant === 'cancelled') && authFetch && (item.cancelled_at || trashed) && item.refund?.status !== 'refunded' && (
          <RebookButton item={item} authFetch={authFetch} onDone={onRebooked} />
        )}
        {variant === 'trash' && onRestore && (
          <button onClick={() => onRestore(item)} className="inline-flex items-center gap-1.5 bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-restore-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </button>
        )}
        {variant === 'trash' && onPermanentDelete && (
          <button onClick={() => onPermanentDelete(item)} className="inline-flex items-center gap-1.5 bg-white border border-red-200 hover:border-red-300 text-red-700 text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-permanent-delete-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete permanently
          </button>
        )}
        {onDelete && variant !== 'trash' && (
          <button onClick={() => onDelete(item)} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-red-300 text-slate-500 hover:text-red-700 text-xs font-bold px-3 py-1.5 rounded-md ml-auto" data-testid="admin-delete-btn" title="Delete booking">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        {item.phone && (
          <a href={customerWhatsApp(item)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
        )}
      </div>
    </div>
  );
};

const Section = ({ id, title, count, children, emoji, tone = 'default', empty, action }) => (
  <section id={id} className="mb-8 scroll-mt-28">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">
        <span className="mr-1">{emoji}</span>{title}
        <span className={`ml-2 text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${tone === 'warn' ? 'bg-amber-100 text-amber-900' : tone === 'live' ? 'bg-emerald-100 text-emerald-900' : tone === 'bad' ? 'bg-red-100 text-red-900' : 'bg-slate-100 text-slate-700'}`}>{count}</span>
      </h2>
      {action}
    </div>
    <div className="space-y-3">
      {count === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">{empty}</div>
      )}
      {children}
    </div>
  </section>
);

/* -------- Drivers management -------- */
const DriverAvatar = ({ d, size = 36 }) => {
  const px = `${size}px`;
  if (d.avatar_url) {
    return (
      <img
        src={resolvePhoto(d.avatar_url)}
        alt={d.name}
        className="rounded-full object-cover shrink-0 border-2 border-white shadow-sm"
        style={{ width: px, height: px, borderColor: d.color }}
        data-testid="driver-avatar-img"
      />
    );
  }
  return (
    <span
      className="rounded-full text-white flex items-center justify-center font-black shrink-0"
      style={{ background: d.color, width: px, height: px, fontSize: Math.max(11, Math.floor(size / 2.4)) }}
      data-testid="driver-avatar-letter"
    >
      {(d.name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
};

const DriverRow = ({ d, authFetch, onChanged, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: d.name, email: d.email, phone: d.phone || '', password: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reinviting, setReinviting] = useState(false);
  const fileRef = React.useRef(null);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {};
      if (form.name !== d.name) payload.name = form.name;
      if (form.email !== d.email) payload.email = form.email.trim().toLowerCase();
      if (form.phone !== (d.phone || '')) payload.phone = form.phone;
      if (form.password) payload.password = form.password;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save.');
        setEditing(false);
        return;
      }
      await authFetch('patch', `/admin/drivers/${d.id}`, payload);
      toast.success(`Updated ${form.name}${form.password ? ' · password reset' : ''}.`);
      setEditing(false);
      setForm({ ...form, password: '' });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <>
    <div className="rounded-xl border border-slate-200 bg-white p-3 hover:border-[color:var(--mv-orange)] transition-colors" data-testid="admin-driver-row">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setEditing(true)} className="flex-1 min-w-0 flex items-center gap-3 text-left" data-testid="admin-driver-open">
          <DriverAvatar d={d} size={40} />
          <div className="min-w-0">
            <div className="font-black text-[color:var(--mv-navy)] truncate">{d.name}</div>
            <div className="text-xs text-slate-500 truncate">{d.phone || d.email}</div>
          </div>
        </button>
        <button onClick={() => setEditing(true)} className="w-9 h-9 rounded-md inline-flex items-center justify-center text-[color:var(--mv-navy)] hover:bg-slate-100" aria-label="Edit" data-testid="admin-driver-edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onRemove(d)} className="w-9 h-9 rounded-md inline-flex items-center justify-center text-red-600 hover:bg-red-50" aria-label="Remove" data-testid="admin-driver-remove"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setEditing(false)} data-testid="driver-profile">
        <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)] truncate">{d.name}</h3>
              <div className="text-xs text-slate-600 flex items-center flex-wrap gap-x-3">
                <a href={`mailto:${d.email}`} className="flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</a>
                {d.phone && <a href={`tel:${d.phone}`} className="flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</a>}
              </div>
            </div>
            <button type="button" onClick={() => setEditing(false)} className="w-10 h-10 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="driver-profile-close"><XIcon className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <DriverAvatar d={d} size={56} />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB.'); return; }
                setUploading(true);
                try {
                  const b64 = await new Promise((res, rej) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result);
                    r.onerror = rej;
                    r.readAsDataURL(f);
                  });
                  await authFetch('post', `/admin/drivers/${d.id}/avatar`, { photo: b64 });
                  toast.success('Avatar updated.');
                  onChanged();
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail));
                } finally {
                  setUploading(false);
                  if (fileRef.current) fileRef.current.value = '';
                }
              }}
              data-testid="driver-avatar-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-slate-400 text-[color:var(--mv-navy)] text-xs font-bold px-3 py-1.5 rounded-md"
              data-testid="driver-avatar-upload"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
              {d.avatar_url ? 'Change photo' : 'Upload photo'}
            </button>
            {d.avatar_url && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Remove this driver\u2019s profile photo?')) return;
                  try {
                    await authFetch('delete', `/admin/drivers/${d.id}/avatar`);
                    toast.success('Photo removed.');
                    onChanged();
                  } catch (err) {
                    toast.error(formatApiError(err?.response?.data?.detail));
                  }
                }}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 px-2"
                data-testid="driver-avatar-remove"
              >
                <XIcon className="w-3.5 h-3.5" /> Remove
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                const pw = window.prompt('Set a new starter password to email them (leave blank to send a random secure one):', '');
                if (pw === null) return;
                if (pw && pw.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
                setReinviting(true);
                try {
                  await authFetch('post', `/admin/drivers/${d.id}/resend-invite`, { password: pw });
                  toast.success('Invite email sent.');
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail));
                } finally {
                  setReinviting(false);
                }
              }}
              className="inline-flex items-center gap-1.5 bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-xs font-bold px-3 py-1.5 rounded-md ml-auto"
              data-testid="driver-resend-invite"
            >
              {reinviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Resend invite
            </button>
          </div>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="driver-edit-name" />
            <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="driver-edit-email" />
            <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="driver-edit-phone" />
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="h-10 w-full rounded-md border border-slate-200 pl-9 pr-3 text-sm" placeholder="New password (leave blank to keep)" type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="driver-edit-password" />
            </div>
            <button type="submit" disabled={saving} className="sm:col-span-2 h-10 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="driver-edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save changes
            </button>
          </form>
        </div>
        </div>
      )}
    </>
  );
};

const DriversPanel = ({ authFetch, token, onDriversChanged, drivers }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [saving, setSaving] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await authFetch('post', '/admin/drivers', form);
      toast.success(`Driver ${form.name} created.`);
      setForm({ name: '', email: '', phone: '', password: '' });
      setShowForm(false);
      onDriversChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove driver ${d.name}? Their assignments will be unassigned.`)) return;
    try {
      await authFetch('delete', `/admin/drivers/${d.id}`);
      toast.success(`Removed ${d.name}.`);
      onDriversChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <Section
      id="sec-drivers"
      title="Drivers"
      count={drivers.length}
      emoji="👥"
      tone="default"
      empty="No drivers yet — add your first below."
      action={
        <button onClick={() => setShowForm(v => !v)} className="inline-flex items-center gap-1.5 bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-md" data-testid="admin-add-driver-btn">
          <Plus className="w-3.5 h-3.5" /> {showForm ? 'Cancel' : 'Add driver'}
        </button>
      }
    >
      {showForm && (
        <form onSubmit={create} className="rounded-xl border border-slate-200 bg-white p-4 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-[color:var(--mv-orange)]" />
            A welcome email with the login link and starter password is sent automatically.
          </div>
          <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="driver-name-input" />
          <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="driver-email-input" />
          <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Phone (UK)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required data-testid="driver-phone-input" />
          <input className="h-10 rounded-md border border-slate-200 px-3 text-sm" placeholder="Starter password" type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required data-testid="driver-password-input" />
          <button type="submit" disabled={saving} className="sm:col-span-2 h-10 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="driver-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create driver
          </button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="driver-list">
        {drivers.map(d => (
          <DriverRow key={d.id} d={d} authFetch={authFetch} onChanged={onDriversChanged} onRemove={remove} />
        ))}
      </div>
    </Section>
  );
};

/* -------- Search bar with floating result dropdown -------- */
const SearchDropdown = ({ query, onQuery, items, drivers, onPick }) => {
  const [open, setOpen] = useState(false);
  const results = query.trim() ? items.slice(0, 50) : [];
  const close = () => setTimeout(() => setOpen(false), 150);

  return (
    <div className="relative mb-5" data-testid="admin-search-wrap">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      <input
        type="text"
        value={query}
        onChange={(e) => { onQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        placeholder="Search any detail — name, postcode, ref, email, phone, notes, service…"
        className="w-full h-11 pl-10 pr-10 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
        data-testid="admin-search"
      />
      {query && (
        <button onMouseDown={(e) => { e.preventDefault(); onQuery(''); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10" data-testid="admin-search-clear"><XIcon className="w-4 h-4" /></button>
      )}

      {open && query.trim() && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-96 overflow-y-auto" data-testid="admin-search-results">
          {results.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">No matches — try a different search.</div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {results.length} match{results.length === 1 ? '' : 'es'} · click to view
              </div>
              {results.map(it => {
                const d = drivers?.find(x => x.id === it.driver_id);
                const waste = isWasteJob(it);
                return (
                  <button
                    key={it.id}
                    onMouseDown={(e) => { e.preventDefault(); onPick(it); }}
                    className="w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 last:border-b-0"
                    data-testid="admin-search-result"
                  >
                    <span className={`w-2 h-10 rounded-full ${waste ? 'bg-violet-600' : 'bg-amber-300'}`}></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-[color:var(--mv-navy)]">#{it.ref}</strong>
                        <span className="text-slate-700 truncate">{it.name || '—'}</span>
                        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${waste ? 'bg-violet-100 text-violet-800' : 'bg-amber-100 text-amber-900'}`}>{waste ? 'Waste' : 'A-B'}</span>
                        {it.deleted_at && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Trashed</span>}
                        {it.cancelled_at && !it.deleted_at && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-800">Cancelled</span>}
                        {it.completed_at && !it.cancelled_at && !it.deleted_at && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Done</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {it.phone || '—'} · {it.from_postcode || '—'} → {it.to_postcode || '—'} · {it.service || '—'}
                      </div>
                      {it.booking ? (
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs" data-testid="admin-search-booking-line">
                          <span className="font-black uppercase text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Booked in</span>
                          <span className="inline-flex items-center gap-1 font-bold text-[color:var(--mv-navy)]"><Clock className="w-3 h-3" />{new Date(it.booking.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · {it.booking.time || '—'}{it.hours_estimate ? ` · ${it.hours_estimate}` : ''}</span>
                          <span className={`inline-flex items-center gap-1 font-bold ${d ? '' : 'text-red-700'}`} style={d ? { color: d.color } : undefined}><Truck className="w-3 h-3" />{d ? d.name : 'No driver yet'}</span>
                        </div>
                      ) : <div className="mt-1 text-[11px] text-slate-400">Not booked in yet</div>}
                    </div>
                    <div className="font-black text-[color:var(--mv-navy)] whitespace-nowrap">£{it.booking?.final_price ?? it.price}</div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* -------- Dashboard -------- */
function Dashboard() {
  const { user, token, authFetch, logout } = useAuth();
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showDrivers, setShowDrivers] = useState(false);
  const [query, setQuery] = useState('');
  const [upcomingView, setUpcomingView] = useState('list'); // 'list' | 'timeline'
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelect = useCallback((id, on) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Move ${ids.length} booking${ids.length === 1 ? '' : 's'} to Trash?\nYou can restore them later.`)) return;
    try {
      const { data } = await authFetch('post', '/admin/estimates/bulk-delete', { ids });
      toast.success(`Moved ${data.deleted} to Trash.`);
      clearSelection();
      setSelectMode(false);
      load(true);
    } catch { toast.error("Couldn't delete selection."); }
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [dash, drv] = await Promise.all([
        authFetch('get', '/admin/dashboard'),
        authFetch('get', '/admin/drivers'),
      ]);
      setData(dash.data);
      setDrivers(drv.data.drivers || []);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); } else { toast.error("Couldn't load the dashboard."); }
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [authFetch, logout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 60_000); return () => clearInterval(t); }, [load]);

  const confirmBooking = async (item) => {
    try { await authFetch('post', `/admin/estimate/${item.id}/confirm`); toast.success(`#${item.ref} confirmed.`); load(true); }
    catch { toast.error("Couldn't update that booking."); }
  };

  const assignDriver = async (item, driverId) => {
    try { await withOverride((override_reason) => authFetch('post', `/admin/estimate/${item.id}/assign`, { driver_id: driverId, override_reason })); toast.success(driverId ? 'Driver assigned.' : 'Unassigned.'); load(true); }
    catch (e) { if (!e.cancelled) toast.error(errMsg(e, "Couldn't assign driver.")); load(true); }
  };

  const cancelBooking = async (item, payload) => {
    try {
      await authFetch('post', `/admin/estimate/${item.id}/cancel`, payload);
      toast.success(`#${item.ref} cancelled. Driver slot freed.`);
      load(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      throw e;
    }
  };

  const uncancelBooking = async (item) => {
    if (!window.confirm(`Restore booking #${item.ref}? You'll need to re-assign a driver manually.`)) return;
    try { await authFetch('post', `/admin/estimate/${item.id}/uncancel`); toast.success(`#${item.ref} restored.`); load(true); }
    catch { toast.error("Couldn't restore that booking."); }
  };

  const markPaid = async (item) => {
    try { const res = await authFetch('post', `/admin/estimate/${item.id}/mark-paid`); toast.success(`#${item.ref} ${paidToast(res?.data)}`); load(true); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : "Couldn't mark as paid."); }
  };

  const markUnpaid = async (item) => {
    if (!window.confirm(`Unmark #${item.ref} as paid?`)) return;
    try { await authFetch('post', `/admin/estimate/${item.id}/mark-unpaid`); toast.success('Deposit unmarked.'); load(true); }
    catch { toast.error("Couldn't unmark."); }
  };

  const completeBooking = async (item) => {
    if (!window.confirm(`Mark booking #${item.ref} as completed?\nThis counts towards today's revenue.`)) return;
    try { await authFetch('post', `/admin/estimate/${item.id}/complete`); toast.success(`#${item.ref} completed · £${item.booking?.final_price ?? item.price} added to revenue.`); load(true); }
    catch { toast.error("Couldn't complete."); }
  };

  const uncompleteBooking = async (item) => {
    try { await authFetch('post', `/admin/estimate/${item.id}/uncomplete`); toast.success(`#${item.ref} reopened.`); load(true); }
    catch { toast.error("Couldn't reopen."); }
  };

  const deleteBooking = async (item) => {
    if (!window.confirm(`Move booking #${item.ref} to Trash?\nYou can restore it later.`)) return;
    try { await authFetch('delete', `/admin/estimate/${item.id}`); toast.success(`#${item.ref} moved to Trash.`); load(true); }
    catch { toast.error("Couldn't delete."); }
  };

  const restoreFromTrash = async (item) => {
    try { await authFetch('post', `/admin/estimate/${item.id}/restore`); toast.success(`#${item.ref} restored.`); load(true); }
    catch { toast.error("Couldn't restore."); }
  };

  const permanentDelete = async (item) => {
    if (!window.confirm(`Permanently delete booking #${item.ref}?\nThis cannot be undone.`)) return;
    try { await authFetch('delete', `/admin/estimate/${item.id}/permanent`); toast.success(`#${item.ref} permanently deleted.`); load(true); }
    catch { toast.error("Couldn't delete."); }
  };

  const emptyTrash = async () => {
    if (!window.confirm("Permanently delete EVERYTHING in Trash?\nThis cannot be undone.")) return;
    try {
      const { data } = await authFetch('post', '/admin/trash/empty');
      toast.success(`Trash emptied · ${data.deleted} removed.`);
      load(true);
    } catch { toast.error("Couldn't empty trash."); }
  };

  const saveStaffNotes = async (item, notes) => {
    try {
      await authFetch('patch', `/admin/estimate/${item.id}/staff-notes`, { notes });
      toast.success('Staff notes saved.');
      load(true);
    } catch { toast.error("Couldn't save notes."); }
  };

  const saveBookingDate = async ({ date, time, notify }) => {
    if (!editTarget.booking) {
      try {
        const { data: r } = await authFetch('post', `/admin/estimate/${editTarget.id}/schedule`, { date, time, notify });
        toast.success(`#${editTarget.ref} added to the calendar${r.driver_id ? ' and a driver assigned' : ' — no driver free, assign one'}.`);
        if (notify) toast.success('Booking confirmation emailed to the customer.');
        load(true);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
        throw e;
      }
      return;
    }
    try {
      await authFetch('patch', `/admin/estimate/${editTarget.id}/booking`, { date, time });
      toast.success(`#${editTarget.ref} rescheduled.`);
      if (notify) {
        try {
          await authFetch('post', `/admin/estimate/${editTarget.id}/notify-update`);
          toast.success('Customer notified by email.');
        } catch { toast.error("Couldn't send update email."); }
      }
      load(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      throw e;
    }
  };

  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const downloadCsv = (scope) => {
    const url = `${API}/admin/export.csv?scope=${scope}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error('download failed');
        const blob = await r.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `move-van-ltd-${scope}-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error("Couldn't download CSV."));
  };

  // Live search filter — matches name, ref, email, phone, postcodes
  const filterFn = useCallback((item) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return [
      item.name, item.ref, item.email, item.phone,
      item.from_postcode, item.to_postcode,
      item.notes, item.service, item.van_recommendation,
    ].some(v => (v || '').toString().toLowerCase().includes(q));
  }, [query]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="flex items-center gap-3 text-slate-600"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /> Loading…</div></div>;
  if (!data) return null;
  const c = data.counts || {};
  const awaiting = (data.awaiting_deposit || []).filter(filterFn);
  const paidNotBooked = (data.paid_not_booked || []).filter(filterFn);
  const toCheck = (data.deposits_to_check || []).filter(filterFn);
  const today = (data.bookings_today || []).filter(filterFn);
  const allBookings = (data.bookings_all || []).filter(filterFn);
  const upcoming = [
    ...allBookings.filter(b => (b.booking?.date || '') >= (data.today_date || '')),
    ...allBookings.filter(b => (b.booking?.date || '') < (data.today_date || '')).reverse(),
  ];
  const firstPastIdx = upcoming.findIndex(b => (b.booking?.date || '') < (data.today_date || ''));
  const quotes = (data.quotes_today || []).filter(filterFn);
  const cancelled = (data.cancelled || []).filter(filterFn);
  const completed = (data.completed || []).filter(filterFn);
  const trash = (data.trash || []).filter(filterFn);
  // Union for search dropdown (de-dupe by id, newest first by created_at)
  const allItemsForSearch = (() => {
    const map = new Map();
    [...quotes, ...today, ...upcoming, ...awaiting, ...paidNotBooked, ...completed, ...cancelled, ...trash].forEach(x => {
      if (!map.has(x.id)) map.set(x.id, x);
    });
    return [...map.values()].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  })();

  // Pass select-mode props into every card
  const selProps = (item) => ({
    selectable: selectMode,
    selected: selectedIds.has(item.id),
    onToggleSelect: toggleSelect,
  });

  const statCards = [
    { id: 'sec-awaiting', label: 'Awaiting deposit', value: c.awaiting_deposit || 0, cls: 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100', testid: 'stat-awaiting' },
    { id: 'sec-paid-not-booked', label: 'Paid, not booked', value: c.paid_not_booked || 0, cls: 'bg-rose-50 border-rose-300 text-rose-900 hover:bg-rose-100 ring-2 ring-rose-200', testid: 'stat-paid-not-booked' },
    { id: 'sec-today', label: 'Bookings today', value: c.bookings_today || 0, cls: 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100', testid: 'stat-today' },
    { id: 'sec-upcoming', label: 'All bookings ever', value: data.bookings_ever ?? (data.bookings_all || []).length, sub: `${(data.bookings_all || []).filter(b => (b.booking?.date || '') >= (data.today_date || '')).length} upcoming · ${(data.bookings_all || []).filter(b => (b.booking?.date || '') < (data.today_date || '')).length} past${(data.bookings_ever ?? 0) > (data.bookings_all || []).length ? ` · ${data.bookings_ever - (data.bookings_all || []).length} no date yet` : ''}`, cls: 'bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100', testid: 'stat-upcoming' },
    { id: 'sec-quotes', label: 'Quotes today', value: c.quotes_today || 0, cls: 'bg-slate-50 border-slate-200 text-slate-800 hover:bg-slate-100', testid: 'stat-quotes' },
    { id: 'sec-availability', label: 'Availability (7d)', value: '👀', cls: 'bg-teal-50 border-teal-200 text-teal-900 hover:bg-teal-100', testid: 'stat-availability' },
    { id: 'sec-cancelled', label: 'Cancelled (30d)', value: c.cancelled || 0, cls: 'bg-red-50 border-red-200 text-red-900 hover:bg-red-100', testid: 'stat-cancelled' },
    { id: 'sec-completed', label: 'Completed (30d)', value: c.completed || 0, cls: 'bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100', testid: 'stat-completed' },
    { id: 'sec-trash', label: 'Trash (30d)', value: c.trash || 0, cls: 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200', testid: 'stat-trash' },
  ];

  return (
    <div className="min-h-[60vh] bg-slate-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Owner dashboard</div>
            <h1 className="font-display text-3xl font-extrabold text-[color:var(--mv-navy)] mt-1">Move Van Ltd · Today</h1>
            <div className="text-xs text-slate-500 mt-1">{data.today_date} · signed in as <strong>{user?.email}</strong> · auto-refresh 60s</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap [&>*]:whitespace-nowrap">
            <Link to="/admin/staff" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-staff-link">
              <PoundSterling className="w-3.5 h-3.5" /> Staff & Wages
            </Link>
            <button type="button" onClick={() => setShowDrivers(true)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-drivers-btn">
              <Truck className="w-3.5 h-3.5" /> Drivers
            </button>
            <Link to="/admin/porters" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-porters-link">
              <Users className="w-3.5 h-3.5" /> Porters
            </Link>
            <Link to="/admin/profit" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid="admin-profit-link">
              <PoundSterling className="w-3.5 h-3.5" /> Company profit
            </Link>
            <Link to="/admin/register" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 text-white text-xs font-bold" data-testid="admin-register-link">
              <ClipboardCheck className="w-3.5 h-3.5" /> Register
            </Link>
            {(data.deposits_to_check || []).length > 0 && (
              <a href="#sec-deposits-to-check" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold" data-testid="admin-deposit-check-badge" aria-label={`${(data.deposits_to_check || []).length} deposits waiting to be checked in Tide`}>
                Deposits to check <span className="min-w-[20px] h-5 px-1 rounded-full bg-white text-orange-700 inline-flex items-center justify-center text-[11px] font-black" data-testid="admin-deposit-check-count">{(data.deposits_to_check || []).length}</span>
              </a>
            )}
            <Link to="/admin/tomorrow" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-tomorrow-link">
              <CalendarClock className="w-3.5 h-3.5" /> Tomorrow's work
            </Link>
            <Link to="/admin/reports" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-reports-link">
              <Truck className="w-3.5 h-3.5" /> Driver reports
            </Link>
            <Link to="/admin/calendar" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-[color:var(--mv-orange)] hover:text-[color:var(--mv-orange)] text-[color:var(--mv-navy)] text-xs font-bold" data-testid="admin-calendar-link">
              <CalIcon className="w-3.5 h-3.5" /> Calendar
            </Link>
            {selectMode ? (
              <>
                <button onClick={bulkDelete} disabled={selectedIds.size === 0} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold" data-testid="admin-bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete {selectedIds.size || ''}
                </button>
                <button onClick={() => { setSelectMode(false); clearSelection(); }} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-bold">
                  <XIcon className="w-3.5 h-3.5" /> Cancel
                </button>
              </>
            ) : (
              <button onClick={() => setSelectMode(true)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-slate-200 hover:border-red-300 hover:text-red-700 text-slate-600 text-xs font-bold" data-testid="admin-select-mode-btn" title="Select multiple to delete">
                <Trash2 className="w-3.5 h-3.5" /> Select
              </button>
            )}
            <div className="relative group">
              <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-xs font-bold" data-testid="admin-export-btn">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-48 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-10">
                <button onClick={() => downloadCsv('today')} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">Today's bookings</button>
                <button onClick={() => downloadCsv('upcoming')} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">Next 14 days</button>
                <button onClick={() => downloadCsv('quotes-today')} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">Today's quotes</button>
              </div>
            </div>
            <button onClick={() => load(true)} disabled={refreshing} className="h-9 w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center justify-center" title="Refresh" data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={logout} className="text-xs font-semibold text-slate-500 hover:text-[color:var(--mv-orange)] px-3 py-2" data-testid="admin-logout-btn">Sign out</button>
          </div>
        </div>

        <RevenueStrip revenue={data.revenue} />
        <OfferTapsCard authFetch={authFetch} />


        {/* Search bar with floating result dropdown */}
        <SearchDropdown
          query={query}
          onQuery={setQuery}
          items={allItemsForSearch}
          drivers={drivers}
          onPick={(it) => { setDetailId(it.id); setQuery(''); }}
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-3 mb-8">
          {statCards.map((card) => (
            <button
              key={card.id}
              type="button"
              onClick={() => scrollTo(card.id)}
              className={`rounded-xl border p-3 text-left transition cursor-pointer ${card.cls}`}
              data-testid={card.testid}
            >
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{card.label}</div>
              <div className="font-display text-2xl font-black mt-1 leading-none" data-testid={`${card.testid}-value`}>{card.value}</div>
              <div className="text-[9px] uppercase tracking-wider font-bold opacity-60 mt-1">{card.sub || 'Tap to jump'}</div>
            </button>
          ))}
        </div>

        {showDrivers && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={() => setShowDrivers(false)} data-testid="drivers-modal">
            <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-5xl bg-slate-50 rounded-xl p-5 pr-14 mt-10">
              <button type="button" onClick={() => setShowDrivers(false)} className="absolute right-3 top-3 w-10 h-10 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="drivers-modal-close"><XIcon className="w-5 h-5" /></button>
              <DriversPanel authFetch={authFetch} token={token} drivers={drivers} onDriversChanged={() => load(true)} />
              <VanRota authFetch={authFetch} />
            </div>
          </div>
        )}

        <Section id="sec-availability" title="Driver availability (next 7 days)" count="" emoji="📊" tone="default" empty="">
          <AvailabilityGrid availability={data.availability} />
        </Section>

        <DepositRefunds authFetch={authFetch} />
        <CancellationReport authFetch={authFetch} />

        <Section id="sec-deposits-to-check" title="Deposits to check in Tide" count={toCheck.length} emoji="🔎" tone="warn" empty={query ? 'No matches.' : 'All customer payments have been checked.'}>
          {toCheck.map(item => <DepositCheckRow key={item.id} item={item} onMarkPaid={markPaid} onView={(x) => setDetailId(x.id)} />)}
        </Section>

        <Section id="sec-paid-not-booked" title="Paid, not yet booked" count={paidNotBooked.length} emoji="🚨" tone="bad" empty={query ? 'No matches.' : 'Perfect — everyone who paid has a slot.'}>
          {paidNotBooked.map(item => <QuoteCard key={item.id} item={item} variant="paid-not-booked" onSchedule={setEditTarget} onView={(x) => setDetailId(x.id)} onMarkUnpaid={markUnpaid} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section id="sec-awaiting" title="Awaiting deposit" count={awaiting.length} emoji="⚡" tone="warn" empty={query ? 'No matches in Awaiting.' : "No one's waiting on us right now — nice."}>
          {awaiting.map(item => <QuoteCard key={item.id} item={item} variant="awaiting" onView={(x) => setDetailId(x.id)} onMarkPaid={markPaid} onMarkUnpaid={markUnpaid} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section id="sec-today" title="Bookings today" count={today.length} emoji="📅" tone="live" empty={query ? 'No matches in Today.' : 'No bookings for today yet.'}>
          {today.map(item => <QuoteCard key={item.id} item={item} variant="booking" onConfirm={confirmBooking} drivers={drivers} onAssign={assignDriver} onCancel={setCancelTarget} onView={(x) => setDetailId(x.id)} onComplete={completeBooking} onEditDate={setEditTarget} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section
          id="sec-upcoming"
          title="All bookings"
          count={upcoming.length}
          emoji="📆"
          empty={query ? 'No matching bookings.' : 'No bookings yet.'}
          action={
            <div className="flex items-center bg-white border border-slate-200 rounded-md p-0.5" data-testid="upcoming-view-toggle">
              <button onClick={() => setUpcomingView('list')} className={`px-2 py-1 rounded text-[11px] font-bold inline-flex items-center gap-1 ${upcomingView === 'list' ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`}><LayoutList className="w-3 h-3" />List</button>
              <button onClick={() => setUpcomingView('timeline')} className={`px-2 py-1 rounded text-[11px] font-bold inline-flex items-center gap-1 ${upcomingView === 'timeline' ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`}><CalIcon className="w-3 h-3" />Timeline</button>
            </div>
          }
        >
          {upcomingView === 'timeline'
            ? <Timeline bookings={upcoming} drivers={drivers} onView={(id) => setDetailId(id)} />
            : upcoming.map((item, i) => <React.Fragment key={item.id}>
                {i === firstPastIdx && <div className="mt-6 mb-2 pt-3 border-t-2 border-dashed border-slate-300 text-xs font-black uppercase tracking-wider text-slate-500" data-testid="all-bookings-past-divider">Past bookings · newest first</div>}
                {(i === 0 || upcoming[i - 1].booking?.date !== item.booking?.date) && <DayHeader date={item.booking?.date} todayIso={data.today_date} />}
                <QuoteCard item={item} variant="booking" onConfirm={confirmBooking} drivers={drivers} onAssign={assignDriver} onCancel={setCancelTarget} onView={(x) => setDetailId(x.id)} onComplete={completeBooking} onEditDate={setEditTarget} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />
              </React.Fragment>)
          }
        </Section>

        <AdminEnquiries authFetch={authFetch} />

        <Section id="sec-quotes" title="All quotes today" count={quotes.length} emoji="📝" empty={query ? 'No matches in today\'s quotes.' : 'No quotes yet today.'}>
          {quotes.map(item => <QuoteCard key={item.id} item={item} onView={(x) => setDetailId(x.id)} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section id="sec-cancelled" title="Cancelled bookings (last 30 days)" count={cancelled.length} emoji="🚫" tone="bad" empty={query ? 'No cancelled matches.' : 'No cancellations in the last 30 days.'}>
          {cancelled.map(item => <QuoteCard key={item.id} item={item} variant="cancelled" authFetch={authFetch} onRebooked={() => load(true)} onUncancel={uncancelBooking} onView={(x) => setDetailId(x.id)} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section id="sec-completed" title="Completed bookings (last 30 days)" count={completed.length} emoji="✅" tone="default" empty={query ? 'No completed matches.' : 'No completed bookings yet — tap "Done" on a booking to log revenue.'}>
          {completed.map(item => <QuoteCard key={item.id} item={item} variant="booking" onView={(x) => setDetailId(x.id)} onComplete={uncompleteBooking} onDelete={deleteBooking} onSaveStaffNotes={saveStaffNotes} {...selProps(item)} />)}
        </Section>

        <Section
          id="sec-trash"
          title="Trash (last 30 days)"
          count={trash.length}
          emoji="🗑️"
          tone="default"
          empty={query ? 'No trashed matches.' : 'Trash is empty.'}
          action={trash.length > 0 ? (
            <button onClick={emptyTrash} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-red-200 hover:border-red-300 text-red-700 text-xs font-bold" data-testid="admin-empty-trash">
              <Trash2 className="w-3.5 h-3.5" /> Empty trash
            </button>
          ) : null}
        >
          {trash.map(item => <QuoteCard key={item.id} item={item} variant="trash" authFetch={authFetch} onRebooked={() => load(true)} onView={(x) => setDetailId(x.id)} onRestore={restoreFromTrash} onPermanentDelete={permanentDelete} />)}
        </Section>
      </div>

      {cancelTarget && (
        <CancelModal item={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={(payload) => cancelBooking(cancelTarget, payload)} />
      )}
      {editTarget && (
        <EditBookingModal item={editTarget} onClose={() => setEditTarget(null)} onSave={saveBookingDate} />
      )}
      {detailId && (
        <DetailModal id={detailId} onClose={() => setDetailId(null)} authFetch={authFetch} onAction={() => load(true)} />
      )}
    </div>
  );
}

/* -------- Entry -------- */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>;
  if (!user) return <LoginForm title="Move Van Ltd · Owner" subtitle="Sign in with your admin email and password." expectedRole="owner" />;
  if (user.role !== 'owner') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-slate-600">This account is a driver, not an owner.</p>
          <button onClick={logout} className="mt-3 text-sm font-bold text-[color:var(--mv-orange)]">Sign out</button>
        </div>
      </div>
    );
  }
  return <Dashboard />;
}
